import { FunctionComponent, useContext, useState } from "react"
import { Form, InputGroup } from "react-bootstrap"
import Alert from "react-bootstrap/Alert"
import Button from "react-bootstrap/Button"
import Container from "react-bootstrap/Container"
import Table from "react-bootstrap/Table"
import { useNavigate } from "react-router-dom"
import { AppContext } from "../components/AppContext"
import { Group, RouterExtended, RouterType } from "../data/models"
import useQuery from "../hooks/useQuery"
import useSessionStorage from "../hooks/useSessionStorage"
import DateUtils from "../utils/DateUtils"

interface IRoutersPage {}
const RoutersPage: FunctionComponent<IRoutersPage> = () => {
	const { groups } = useContext(AppContext)
	const navigate = useNavigate()
	const routersQuery = useQuery<void, RoutersOutput>(process.env.REACT_APP_KNIT_URL + "routers")
	const [includePropertyRouters, setIncludePropertyRouters] = useState(false)
	const [propertyFilter, setPropertyFilter] = useSessionStorage("bell.routers.property", "")
	const [unitFilter, setUnitFilter] = useSessionStorage("bell.routers.unit", "")
	const [tenantNameFilter, setTenantNameFilter] = useSessionStorage("bell.routers.tenantName", "")
	const [planNameFilter, setPlanNameFilter] = useSessionStorage("bell.routers.planName", "")
	const [sortProperty, setSortProperty] = useState<string | null>()
	const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC")
	//change sort direction when clicking on header
	const handleSetSortProperty = (property: string) => {
		if (sortProperty === property) {
			setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC")
		} else {
			setSortProperty(property)
			setSortDirection("ASC")
		}
	}
	//filter routers based on user input
	let filteredRouters = routersQuery.data?.routers || []
	if (!includePropertyRouters) {
		filteredRouters = filteredRouters.filter((m) => m.type !== RouterType.Property)
	}
	if (propertyFilter) {
		const filters = propertyFilter.toLowerCase().split(" ")
		for (const filter of filters) {
			filteredRouters = filteredRouters.filter((m) => m.propertyName?.toLowerCase().includes(filter))
		}
	}
	if (unitFilter) {
		const filters = unitFilter.toLowerCase().split(" ")
		for (const filter of filters) {
			filteredRouters = filteredRouters.filter((m) => m.unitName?.toLowerCase().includes(filter))
		}
	}
	if (tenantNameFilter) {
		const filters = tenantNameFilter.toLowerCase().split(" ")
		for (const filter of filters) {
			filteredRouters = filteredRouters.filter((m) => (m.currentTenantName || m.nextTenantName)?.toLowerCase().includes(filter))
		}
	}
	if (planNameFilter) {
		const filters = planNameFilter.toLowerCase().split(" ")
		for (const filter of filters) {
			filteredRouters = filteredRouters.filter((m) => (m.currentPlanName || m.nextPlanName)?.toLowerCase().includes(filter))
		}
	}
	//sort routers based on user input
	if (sortProperty) {
		filteredRouters.sort((a, b) => compareRouters(a, b, sortProperty, sortDirection))
	}
	//
	const openRouter = (router: RouterExtended) => {
		//only admins can open property routers
		const admin = groups?.some((group) => group === Group.InternetManagementAdmin || group === Group.GlobalAdmin)
		if (admin || router.unitId) {
			navigate(`/routers/${router.id}`)
		}
	}
	//
	const loading = !routersQuery.data || routersQuery.loading
	//
	if (loading) {
		return (
			<Container style={{ maxWidth: 500 }}>
				<h2 className="text-center mt-4">Routers</h2>
				<div className="text-center">Loading</div>
			</Container>
		)
	}
	return (
		<Container fluid>
			<h2 className="text-center mt-4">Routers</h2>
			{routersQuery.error && <Alert variant={"warning"}>{routersQuery.error}</Alert>}
			<Form.Check
				type="switch"
				label="Include Property-level Routers"
				checked={includePropertyRouters}
				onChange={() => {
					setIncludePropertyRouters(!includePropertyRouters)
				}}
			/>
			<Table striped bordered hover size="sm">
				<thead>
					<tr>
						<th onClick={() => handleSetSortProperty("propertyName")}>Property</th>
						<th onClick={() => handleSetSortProperty("unitName")}>Unit</th>
						<th onClick={() => handleSetSortProperty("currentTenantName")}>Customer</th>
						<th onClick={() => handleSetSortProperty("currentPlanName")}>Plan</th>
						<th onClick={() => handleSetSortProperty("currentEndDate")}>Plan Date</th>
						<th onClick={() => handleSetSortProperty("statusDate")}>Status</th>
						<th onClick={() => handleSetSortProperty("norhartResponseMilliseconds")}>Norhart</th>
						<th onClick={() => handleSetSortProperty("googleResponseMilliseconds")}>Google</th>
						<th onClick={() => handleSetSortProperty("youTubeResponseMilliseconds")}>Youtube</th>
						<th onClick={() => handleSetSortProperty("wikipediaResponseMilliseconds")}>Wikipedia</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>
							<InputGroup>
								<Form.Control type="text" size="sm" placeholder="Filter Property" value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)} />
								{propertyFilter && (
									<Button variant="outline-secondary" size="sm" onClick={() => setPropertyFilter("")}>
										Clear
									</Button>
								)}
							</InputGroup>
						</td>
						<td>
							<InputGroup>
								<Form.Control type="text" size="sm" placeholder="Filter Unit" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)} />
								{unitFilter && (
									<Button variant="outline-secondary" size="sm" onClick={() => setUnitFilter("")}>
										Clear
									</Button>
								)}
							</InputGroup>
						</td>
						<td>
							<InputGroup>
								<Form.Control
									type="text"
									size="sm"
									placeholder="Filter Customer"
									value={tenantNameFilter}
									onChange={(e) => setTenantNameFilter(e.target.value)}
								/>
								{tenantNameFilter && (
									<Button variant="outline-secondary" size="sm" onClick={() => setTenantNameFilter("")}>
										Clear
									</Button>
								)}
							</InputGroup>
						</td>
						<td>
							<InputGroup>
								<Form.Control type="text" size="sm" placeholder="Filter Plan" value={planNameFilter} onChange={(e) => setPlanNameFilter(e.target.value)} />
								{planNameFilter && (
									<Button variant="outline-secondary" size="sm" onClick={() => setPlanNameFilter("")}>
										Clear
									</Button>
								)}
							</InputGroup>
						</td>
						<td></td>
						<td></td>
						<td></td>
						<td></td>
						<td></td>
						<td></td>
					</tr>
					{filteredRouters?.map((router) => {
						return (
							<tr key={router.id} onClick={() => openRouter(router)}>
								<td>{router.propertyName}</td>
								<td>{router.unitName}</td>
								<td>{router.currentTenantName || router.nextTenantName}</td>
								<td>{router.currentPlanName || router.nextPlanName}</td>
								<td>{makePlanDateString(router)}</td>
								<StatusCell router={router} />
								<PingCell router={router} site="norhart" />
								<PingCell router={router} site="google" />
								<PingCell router={router} site="youtube" />
								<PingCell router={router} site="wikipedia" />
							</tr>
						)
					})}
				</tbody>
			</Table>
			<Button variant="secondary" onClick={() => navigate(-1)}>
				Back
			</Button>
		</Container>
	)
}

export default RoutersPage

const makePlanDateString = (router: RouterExtended) => {
	if (router.currentEndDate) {
		return "Through " + router.currentEndDate
	} else if (router.nextStartDate) {
		return "Starting on " + router.nextStartDate
	}
}

const compareRouters = (a: RouterExtended, b: RouterExtended, sortProperty: string, sortDirection: "ASC" | "DESC") => {
	if (!sortProperty) return 0
	const aProp = (a as any)[sortProperty]
	const bProp = (b as any)[sortProperty]
	if (aProp && !bProp) return -1
	if (!aProp && bProp) return 1
	if (!aProp && !bProp) return 0
	if (aProp < bProp) return sortDirection === "ASC" ? -1 : 1
	if (aProp > bProp) return sortDirection === "ASC" ? 1 : -1
	return 0
}

interface RoutersOutput {
	routers: RouterExtended[]
}

interface IStatusTd {
	router: RouterExtended
}
const StatusCell: FunctionComponent<IStatusTd> = ({ router }) => {
	if (!router.statusDate) {
		return <td style={{ color: "red" }}>Not Installed</td>
	}
	const statusDate = new Date(router.statusDate)
	const aFewMinutesAgo = DateUtils.addMinutes(new Date(), -10)!
	const isLate = statusDate < aFewMinutesAgo
	const statusDateString = statusDate.toLocaleDateString() + " " + statusDate.toLocaleTimeString()
	if (isLate) {
		return <td style={{ color: "red" }}>{statusDateString}</td>
	}
	return <td>{statusDateString}</td>
}

interface IPingCell {
	router: RouterExtended
	site: "norhart" | "google" | "youtube" | "wikipedia"
}
const PingCell: FunctionComponent<IPingCell> = ({ router, site }) => {
	//get ping for site
	let ping: number | undefined = undefined
	if (site === "norhart") ping = router.norhartResponseMilliseconds
	if (site === "google") ping = router.googleResponseMilliseconds
	if (site === "youtube") ping = router.youTubeResponseMilliseconds
	if (site === "wikipedia") ping = router.wikipediaResponseMilliseconds
	if (ping == null) {
		return <td></td>
	}
	//handle units with no plan
	if (router.currentPlanName == null) {
		if (site === "norhart" && ping > 3000) {
			return <td style={{ color: "red" }}>{ping}</td>
		}
		return <td>{ping}</td>
	}
	//handle units with a plan
	if (ping > 1500) {
		return <td style={{ color: "red" }}>{ping}</td>
	}
	return <td>{ping}</td>
}
